import React, { useEffect, useMemo } from "react";
import * as DTO from "../../interfaces";
import "./styles.scss";

import { useAccountSettings, URLS, useSocialCoachSession } from "@/utils";
import { WebComponents } from "@/interfaces";
import { CategoryCard } from "@/components";
import { AssetPreview } from "../CategoryCard/components/AssetPreviewCard";
import { useHistory } from "react-router-dom";
import { PromptCategoryModal } from "./components/PromptCategoryModal";

interface OwnProps {
  promptList: DTO.Prompt[];
  categories: DTO.Category[];
  onClickPrompt?: (prompt: DTO.Prompt) => void;
}

type Props = OwnProps;

export const PromptCategoryCards: React.FC<Props> = ({ promptList, categories, onClickPrompt }) => {
  const otherCategory: DTO.Category = {
    name: "Uncategorized Prompts",
    categoryId: "UNCATEGORIZED",
    type: "LIBRARY",
  };
  const draftCategory: DTO.Category = {
    name: "Unpublished Prompts",
    categoryId: "UNPUBLISHED",
    type: "LIBRARY",
  };

  const [categoriesList, setCategoriesList] = React.useState<DTO.Category[]>([
    draftCategory,
    ...categories,
    otherCategory,
  ]);

  const [selectedCategory, setSelectedCategory] = React.useState<DTO.Category | undefined>(undefined);
  const [showModal, setShowModal] = React.useState<boolean>(false);
  const history = useHistory();
  const { isPlayer } = useSocialCoachSession();
  const { hasWritePermission } = useAccountSettings();
  const withWritePermission = !isPlayer && hasWritePermission(WebComponents.PROMPTS);

  const uncategorizedPrompts = useMemo((): DTO.Prompt[] => {
    return promptList.filter(prompt => {
      return prompt.categories?.length === 0 && prompt.status !== "DRAFT" && prompt.deliveryMode === "LIBRARY";
    });
  }, [promptList]);

  const unpublishedPrompts = useMemo((): DTO.Prompt[] => {
    return promptList.filter(prompt => {
      return prompt.status === "DRAFT" && prompt.deliveryMode === "LIBRARY";
    });
  }, [promptList]);

  const isPseudoCategory = (id?: string) => {
    return id === "UNPUBLISHED" || id === "UNCATEGORIZED";
  };

  useEffect(() => {
    const newList = [draftCategory, ...categories, otherCategory];
    setCategoriesList(newList);
  }, [categories]);

  const promptsForCategory = (category: DTO.Category) => {
    if (category.categoryId == "UNPUBLISHED") return unpublishedPrompts;
    else if (category.categoryId == "UNCATEGORIZED") return uncategorizedPrompts;
    return promptList.filter(prompt => {
      return prompt.categories?.map(c => c.categoryId!).includes(category.categoryId!);
    });
  };

  const toPreview = (prompt: DTO.Prompt) => {
    let thumbnail = undefined;

    const postMedia = prompt.post?.media ? prompt.post?.media[0] : undefined;
    if (postMedia) {
      if (postMedia.category === "IMAGE") {
        thumbnail = postMedia?.uri;
      } else if (postMedia.thumbnailUri) {
        thumbnail = postMedia.thumbnailUri;
      }
    }

    return {
      assetId: prompt.promptId,
      thumbnail,
    } as AssetPreview;
  };

  const clickPrompt = (prompt: DTO.Prompt) => {
    setShowModal(false);
    if (onClickPrompt) {
      onClickPrompt(prompt);
    } else if (!isPlayer) {
      history.push(URLS.coach.prompt.replace(":promptId", prompt.promptId?.toString()!));
    }
  };

  return (
    <div className="promptCardsView">
      <div className="flex flex-wrap gap-3">
        {categoriesList.map(category => {
          return (
            <CategoryCard
              assetList={promptsForCategory(category).map(p => toPreview(p))}
              assetType="Prompt"
              category={category}
              selectCategory={c => {
                setSelectedCategory(c);
                setShowModal(true);
              }}
              selectAsset={asset => {
                clickPrompt(promptList.find(p => p.promptId === asset.assetId)!);
              }}
            />
          );
        })}
      </div>
      <PromptCategoryModal
        open={showModal}
        onClose={() => setShowModal(false)}
        category={selectedCategory}
        prompts={selectedCategory ? promptsForCategory(selectedCategory) : []}
        canEdit={withWritePermission && !isPseudoCategory(selectedCategory?.categoryId)}
        selectPrompt={clickPrompt}
        onUpdate={newCategory => {
          setSelectedCategory(newCategory);
          setCategoriesList(l => l.map(c => (c.categoryId === newCategory.categoryId ? newCategory : c)));
        }}
      />
    </div>
  );
};
